import { insightsIntl } from "../strings/intl";
import { metricDescription } from "../strings/metric-messages";
// eslint-disable-next-line no-restricted-imports
import moment from "moment";
import type { LegacyTableColumn, LegacyTableRow, AdditionalCostsData } from "@wayflyer/flyui-dataviz";
import { MetricReferenceDirection } from "@wayflyer/flyui-dataviz";
import { currencyFormatter } from "shared-utils";

import { formatValue } from "../strings/type-formatting";
import { MetricTypes } from "../autogen/metrics";

import type { Metric, TableData } from "./types";

export const getSort = (key: string) => {
  return `sort_${key}`;
};

export type Align = "left" | "right" | "center";

const metricsWithDecimals = [
  MetricTypes.AOV,
  MetricTypes.ROAS,
  MetricTypes.REFUNDS_PERCENT,
  MetricTypes.NEW_CUSTOMERS,
  MetricTypes.GROSS_MARGIN_PERCENTAGE,
  MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS,
  MetricTypes.AD_COST_PER_CONVERSION,
  MetricTypes.PERCENTAGE_OF_SPEND,
  MetricTypes.AD_ATTRIBUTED_CONVERSION_ROAS,
  MetricTypes.AD_AOV,
  MetricTypes.AD_CONVERSION_RATE,
  MetricTypes.AD_CTR,
  MetricTypes.GOOGLE_AD_ATTRIBUTED_CONVERSION_ROAS,
  MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS,
];

export const includeMetricDecimal = (metricType: MetricTypes) => {
  return metricsWithDecimals.includes(metricType);
};

export const columnForMetric = (metric: Metric) => {
  return {
    name: metricDescription(insightsIntl, metric.name as MetricTypes),
    id: metric.name,
    sortDescFirst: true,
    align: "right" as Align,
  };
};

export const columnsForMetrics = (metrics: Metric[]): LegacyTableColumn[] =>
  metrics.map((metric: Metric) => {
    return columnForMetric(metric);
  });

export const renderMetric = (data: TableData, metricKey: string) => {
  if (data?.data?.length) {
    const metricIndex = data.metrics.findIndex((metric) => metric.name === metricKey);
    const metric = data.metrics[metricIndex];
    const metricValue = data.data[0][metricIndex];
    return formatValue({ value: metricValue, metricType: metric });
  }
  return "-";
};

export const renderDataMetric = (metrics: Metric[], value: string, metricKey: string, insightsScreen?: boolean) => {
  if (value) {
    const metricIndex = metrics.findIndex((metric) => metric.name === metricKey);
    if (metricIndex >= 0) {
      const metric = metrics[metricIndex];
      return formatValue({
        value,
        metricType: metric,
        withDecimal: insightsScreen ? includeMetricDecimal(metric.name as MetricTypes) : undefined,
      });
    }
    return value;
  }
  return "-";
};

const renderDataMetricData = (metric: Metric | null, value: string, insightsScreen?: boolean) => {
  if (value) {
    if (metric) {
      return formatValue({
        value,
        metricType: metric,
        withDecimal: insightsScreen ? includeMetricDecimal(metric.name as MetricTypes) : undefined,
      });
    }
    return value;
  }
  return "-";
};

export type ColumnsAndData = {
  columns: LegacyTableColumn[];
  data: LegacyTableRow[];
};

type DataColMetricIndex = { [dataColIndex: string]: { metric: Metric | null; index: number } };
// index of the filtered/ordered column
type FormatCols = { [index: number]: (val: string | number) => string | number };
type RenameColsHeader = { [index: number]: string };
type MarkerColsHeader = { [index: number]: boolean };

const getValue = (value: string) => (isNaN(+value) || isNaN(parseFloat(value)) ? value : parseFloat(value));

type ColumnsAndMetrics = {
  columns: LegacyTableColumn[];
  metrics: Metric[];
};
const getColumnsAndMetrics = (
  data: TableData,
  isPerformance = false,
  hasLink = false,
  filterAndOrderMetrics: string[] = [],
  renameHeader: RenameColsHeader = {},
  dataColMetricIndex: DataColMetricIndex = {},
  headerWithQuestionMarker: MarkerColsHeader = {}
): ColumnsAndMetrics => {
  let keyIndex = 0;
  const metrics: Metric[] = filterAndOrderMetrics.length
    ? (filterAndOrderMetrics
        .map((metricName) => {
          const metricIndex = data.metrics.findIndex((metric) => metric.name === metricName);
          if (metricIndex === -1) {
            // column not included in data
            return null;
          }
          dataColMetricIndex[data.groupByDimensions.length + metricIndex] = {
            metric: data.metrics[metricIndex],
            index: data.groupByDimensions.length + keyIndex,
          };
          keyIndex++;
          return data.metrics[metricIndex];
        })
        .filter((metric) => metric) as Metric[])
    : data.metrics;

  if (filterAndOrderMetrics.length) {
    data.groupByDimensions.forEach((col, index) => {
      dataColMetricIndex[index] = { metric: null, index };
    });
  }

  const metricCols = columnsForMetrics(metrics);
  // Todo Need a more generic solution to allow for multiple (or none) groupByDimensions being rendered in a table.
  const index = isPerformance ? 1 : 0;
  const groupByDimensionCol = data.groupByDimensions[index]
    ? [
        {
          name: renameHeader[0] ? renameHeader[0] : data.groupByDimensions[index],
          id: data.groupByDimensions[index],
          isLink: hasLink,
          align: "left" as Align,
        },
      ]
    : [];
  return {
    columns: [
      ...groupByDimensionCol,
      ...metricCols.map((metricCol, index) => {
        if (renameHeader[index + 1]) {
          metricCol.name = renameHeader[index + 1];
        }
        if (headerWithQuestionMarker[index + 1]) {
          metricCol.questionMarker = headerWithQuestionMarker[index + 1];
        }

        return metricCol;
      }),
    ],
    metrics,
  };
};

interface GetColumnsAndDataParams {
  data: TableData;
  isPerformance?: boolean;
  hasLink?: boolean;
  filterAndOrderMetrics?: string[];
  formatData?: FormatCols;
  renameHeader?: RenameColsHeader;
  insightsScreen?: boolean;
  headerWithQuestionMarker?: MarkerColsHeader;
}

export const getColumnsAndData = (getColumnsAndDataParams: GetColumnsAndDataParams): ColumnsAndData => {
  const {
    data,
    isPerformance = false,
    hasLink = false,
    filterAndOrderMetrics = [],
    formatData = {},
    renameHeader = {},
    insightsScreen,
    headerWithQuestionMarker = {},
  } = getColumnsAndDataParams;

  const dataColMetricIndex: DataColMetricIndex = {};
  const { columns, metrics } = getColumnsAndMetrics(
    data,
    isPerformance,
    hasLink,
    filterAndOrderMetrics,
    renameHeader,
    dataColMetricIndex,
    headerWithQuestionMarker
  );
  const metricKeys = metrics.map((metric) => metric.name);
  const keys = [...data.groupByDimensions, ...metricKeys];

  const groupedData = data.data.map((rowData) => {
    const newRowData: LegacyTableRow = {};
    let colIndex = 0;
    rowData.forEach((value, index) => {
      if (filterAndOrderMetrics.length) {
        if (dataColMetricIndex[index]) {
          newRowData[keys[dataColMetricIndex[index].index]] = renderDataMetricData(
            dataColMetricIndex[index].metric,
            value,
            insightsScreen
          );
          if (formatData[colIndex]) {
            newRowData[keys[dataColMetricIndex[index].index]] = formatData[colIndex](
              newRowData[keys[dataColMetricIndex[index].index]]
            );
          }
          newRowData[`sort_${keys[dataColMetricIndex[index].index]}`] = getValue(value);
          colIndex++;
        }
      } else {
        newRowData[keys[index]] = renderDataMetric(data.metrics, value, keys[index], insightsScreen);
        if (formatData[index]) {
          newRowData[keys[index]] = formatData[index](newRowData[keys[index]]);
        }
        newRowData[`sort_${keys[index]}`] = getValue(value);
      }
    });
    return newRowData;
  });

  return { columns, data: groupedData };
};

export const formatWeek = (value: string | number): string => {
  const start = moment(value.toString());
  const end = start.clone().add(6, "days");
  if (start.format("MM") !== end.format("MM")) {
    return `${start.format("YYYY MMM D")}${end.format(" - MMM D")}`;
  }
  return `${start.format("YYYY MMM D")}${end.format(" - D")}`;
};

export const getCurrencySymbol = (currencyCode: string): string => {
  return currencyFormatter(currencyCode).format(0).replace("0.00", "");
};

export type ReferenceData = {
  metricReferenceSuccess: boolean;
  metricReferenceDirection: MetricReferenceDirection;
  metricReferenceValue: string;
};

export const hasAdditionalCosts = (additionalCosts: AdditionalCostsData): boolean => {
  // as long as we have a value for at least additional cost
  return Boolean(additionalCosts.cogs || additionalCosts.shipping || additionalCosts.opex || additionalCosts.marketing);
};
